import {useState, useMemo} from "react";
import moment from "moment";
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  Select,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import MyPagination from "src/components/common/MyPagination";
import {asyncRequest} from "src/services/network";

const TABLE_HEAD = [
  {id: "id", label: "ID", alignRight: false, width: 80},
  {id: "admin", label: "操作人", alignRight: false, width: 120},
  {id: "operation", label: "操作类型", alignRight: false, width: 140},
  {id: "target_user", label: "目标用户", alignRight: false, width: 150},
  {id: "detail", label: "操作详情", alignRight: false},
  {id: "remark", label: "备注", alignRight: false, width: 150},
  {id: "create_time", label: "时间", alignRight: false, width: 170},
];

// 操作类型枚举，需与后端 constants.ADMIN_OPERATION_TYPES 保持一致
const OPERATION_OPTIONS = [
  {value: "", label: "全部"},
  {value: "change_xfq_locked", label: "留存权益金修改"},
  {value: "change_xfq", label: "权益金修改"},
  {value: "change_dhq", label: "兑换券修改"},
  {value: "change_txq", label: "体验券修改"},
  {value: "change_balance", label: "余额修改"},
  {value: "change_point", label: "积分修改"},
  {value: "update_user_info", label: "修改用户基础信息"},
  {value: "update_user_status", label: "修改用户状态"},
  {value: "update_inviter", label: "修改用户直推"},
  {value: "update_user_level", label: "修改用户等级"},
  {value: "destroy_user", label: "销毁用户"},
];

const OPERATION_LABEL_MAP = OPERATION_OPTIONS.reduce((acc, cur) => {
  if (cur.value) acc[cur.value] = cur.label;
  return acc;
}, {});

const OPERATION_COLOR_MAP = {
  change_xfq_locked: "warning",
  change_xfq: "warning",
  change_dhq: "info",
  change_txq: "info",
  change_balance: "secondary",
  change_point: "secondary",
  update_user_info: "default",
  update_user_status: "error",
  update_inviter: "info",
  update_user_level: "success",
  destroy_user: "error",
};

const TYPE_LABEL = {PLUS: "增加", MINUS: "减少"};

export default function AdminOperationLogList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);

  const defaultTimeRange = useMemo(() => {
    const now = new Date();
    // endDate 默认 +1 分钟，避免删掉刚刚发生的日志
    const end = moment(now).add(1, "minutes").format("YYYY-MM-DDTHH:mm");
    const start = moment(now).subtract(7, "days").format("YYYY-MM-DDTHH:mm");
    return {startDate: start, endDate: end};
  }, []);

  const [filter, setFilter] = useState({
    page: 0,
    size: 20,
    operation: "",
    adminName: "",
    targetUserId: "",
    startDate: defaultTimeRange.startDate,
    endDate: defaultTimeRange.endDate,
  });

  async function getItems(filterOverride) {
    setItems(null);
    const queryFilter = filterOverride || filter;
    const {data, count} = await asyncRequest({
      path: "/admin-operation-log/list",
      data: queryFilter,
    });
    setItems(data || []);
    setTotal(count);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = {...filter, page: 0, size: newSize};
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = {...filter, page: newPage};
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({
      page: 0,
      size: 20,
      operation: "",
      adminName: "",
      targetUserId: "",
      startDate: defaultTimeRange.startDate,
      endDate: defaultTimeRange.endDate,
    });
  }

  function formatDetail(detail, operation) {
    if (!detail) return "-";
    try {
      const obj = typeof detail === "string" ? JSON.parse(detail) : detail;
      // PLUS/MINUS 变动类
      if (["change_xfq_locked", "change_xfq", "change_dhq", "change_txq", "change_balance", "change_point"].includes(operation)) {
        const typeText = TYPE_LABEL[obj.type] || obj.type || "";
        const amount = obj.amount !== undefined ? obj.amount : "-";
        if (obj.before !== undefined && obj.after !== undefined) {
          return `${typeText} ${amount}（${obj.before} → ${obj.after}）`;
        }
        return `${typeText} ${amount}`;
      }
      if (operation === "update_inviter") {
        return `上级ID：${obj.old_inviter_id ?? "无"} → ${obj.new_inviter_id ?? "无"}`;
      }
      if (operation === "update_user_status") {
        return `状态：${obj.before ?? "-"} → ${obj.after ?? "-"}`;
      }
      if (operation === "update_user_level") {
        return `等级：${obj.before ?? "-"} → ${obj.after ?? "-"}`;
      }
      return JSON.stringify(obj);
    } catch {
      return String(detail);
    }
  }

  return (
    <Page title="后台操作日志">
      <Container maxWidth="xl">
        <Card>
          {/* 搜索栏 */}
          <Stack spacing={2} sx={{p: 2}}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FormControl size="small" style={{minWidth: 160}}>
                  <InputLabel>操作类型</InputLabel>
                  <Select
                    label="操作类型"
                    value={filter.operation}
                    onChange={(e) => setFilter({...filter, operation: e.target.value, page: 0})}
                  >
                    {OPERATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label="操作人"
                  value={filter.adminName}
                  onChange={(e) => setFilter({...filter, adminName: e.target.value, page: 0})}
                  placeholder="管理员名称"
                  autoComplete="off"
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label="目标用户ID"
                  type="number"
                  value={filter.targetUserId}
                  onChange={(e) => setFilter({...filter, targetUserId: e.target.value, page: 0})}
                  placeholder="User UID"
                  autoComplete="off"
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="开始时间"
                  value={filter.startDate}
                  onChange={(e) => setFilter({...filter, startDate: e.target.value, page: 0})}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="结束时间"
                  value={filter.endDate}
                  onChange={(e) => setFilter({...filter, endDate: e.target.value, page: 0})}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => getItems()}>查询</Button>
                  <Button variant="outlined" onClick={handleReset}>重置</Button>
                </Stack>
              </Grid>
            </Grid>
          </Stack>

          <Scrollbar>
            <TableContainer sx={{minWidth: 900}}>
              <Table>
                <ListHead headLabel={TABLE_HEAD}/>
                <TableBody>
                  {items === null ? (
                    <TableRow>
                      <TableCell align="center" colSpan={7}>
                        <Typography variant="body2" color="text.secondary">请点击查询</Typography>
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell align="center" colSpan={7}>
                        <SearchNotFound/>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow hover key={row.id}>
                        <TableCell align="left">{row.id}</TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.admin_name || "-"}</Typography>
                          <Typography variant="caption" color="text.secondary">ID: {row.admin_id}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            size="small"
                            label={OPERATION_LABEL_MAP[row.operation] || row.operation}
                            color={OPERATION_COLOR_MAP[row.operation] || "default"}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.target_user_name || "-"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.target_user_id ? `UID: ${row.target_user_id}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{maxWidth: 350, wordBreak: "break-all"}}>
                            {formatDetail(row.detail, row.operation)}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.remark || "-"}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.create_time ? moment(row.create_time).format("YYYY-MM-DD HH:mm:ss") : "-"}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {total > 0 && (
            <MyPagination
              page={filter.page + 1}
              count={total}
              rowsPerPage={filter.size}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      </Container>
    </Page>
  );
}
